import { InvestorDetailsGetInvestorLegalEntitiesQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { CloseInfoIcon } from '@/assets/icons/info-icons/closeInfo';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Input, { ErrorText } from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { AddButton } from '@/components/dealPipeline-page/modify/addButton';
import { DynamicSelectWrapper } from '@/components/dealPipeline-page/modify/radioWithDynamicSelects';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  EditInvestorFormDevider,
  EditInvestorFormFieldsWrapper,
  EditInvestorFormFooter,
  EditInvestorModalPageForm,
  EditInvestorModalPageWrapper
} from '../../styles';
import { EDIT_INVESTOR_PAGE_ENTITY_GROUPING_CREATE_GROUP_MUTATION, EDIT_INVESTOR_PAGE_ENTITY_GROUPING_UPDATE_GROUP_MUTATION } from './queries';

interface IEditGroupForm {
  groupName: string;
  primaryEntityId: string;
  additionalGroupedEntities: ISelectValue[];
}

const defaultEditGroupFormValues: IEditGroupForm = {
  groupName: '',
  primaryEntityId: '',
  additionalGroupedEntities: []
};

interface EditGroupModalPageProps {
  currentGroup: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'][number] | null;
  investorLegalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
  investorLegalEntitiesRefetch: () => Promise<ApolloQueryResult<InvestorDetailsGetInvestorLegalEntitiesQuery>>;
  handleCloseModalPage: () => void;
}

export const EditGroupModalPage = ({
  currentGroup,
  investorLegalEntities,
  investorLegalEntitiesRefetch,
  handleCloseModalPage
}: EditGroupModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { t } = useTranslation(['editInvestorPage', 'common']);
  const {
    control,
    formState: { isValid, isSubmitting },
    reset,
    watch,
    handleSubmit
  } = useForm<IEditGroupForm>({ defaultValues: defaultEditGroupFormValues });

  const [primaryEntitySelectOptions, setPrimaryEntitySelectOptions] = useState<ISelectValue[]>([]);
  const [listUngroupedEntities, setListUngroupedEntities] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInteractedWithEmptyOption, setHasInteractedWithEmptyOption] = useState<boolean>(false);
  const [isPrimaryEntityChanged, setIsPrimaryEntityChanged] = useState(false);

  const [createGroup] = useMutation(EDIT_INVESTOR_PAGE_ENTITY_GROUPING_CREATE_GROUP_MUTATION);
  const [updateGroup] = useMutation(EDIT_INVESTOR_PAGE_ENTITY_GROUPING_UPDATE_GROUP_MUTATION);

  useEffect(() => {
    // Reset the interaction state when component mounts or currentGroup changes
    setHasInteractedWithEmptyOption(false);

    if (!currentGroup) {
      const selectOptions =
        investorLegalEntities?.filter((entity) => !entity.isGroup)?.map((entity) => createSimpleSelectOption(entity.id, entity.entityName)) || [];

      setListUngroupedEntities(selectOptions);
      setPrimaryEntitySelectOptions(selectOptions);
      return;
    }

    const { entityName } = currentGroup;
    const primaryEntity = currentGroup?.entities?.find((entity) => entity.primaryEntity);
    const currentPrimaryEntitySelectOption = primaryEntity ? createSimpleSelectOption(primaryEntity.id, primaryEntity.entityName) : null;

    const allAvailableOptions = investorLegalEntities
      .filter((entity) => !entity.isGroup)
      .map((entity) => createSimpleSelectOption(entity.id, entity.entityName));

    const additionalGroupedEntities =
      currentGroup?.entities?.filter((entity) => !entity.primaryEntity)?.map((entity) => createSimpleSelectOption(entity.id, entity.entityName)) ||
      [];

    const additionalEntityIds = additionalGroupedEntities.map((entity) => entity.value);
    const filteredPrimaryOptions = allAvailableOptions.filter((option) => !additionalEntityIds.includes(option.value));

    setPrimaryEntitySelectOptions(
      currentPrimaryEntitySelectOption ? [currentPrimaryEntitySelectOption, ...filteredPrimaryOptions] : filteredPrimaryOptions
    );

    const primaryEntityId = primaryEntity?.id;
    setListUngroupedEntities(allAvailableOptions.filter((option) => option.value !== primaryEntityId));

    reset((defaultValues) => ({
      ...defaultValues,
      groupName: entityName,
      primaryEntityId: primaryEntity?.id || '',
      additionalGroupedEntities
    }));
  }, [currentGroup, investorLegalEntities]);

  useEffect(() => {
    if (!currentGroup) return;

    const newPrimaryEntityId = watch('primaryEntityId');
    const primaryEntity = currentGroup?.entities?.find((entity) => entity.primaryEntity);

    setIsPrimaryEntityChanged(primaryEntity?.id !== newPrimaryEntityId);
  }, [watch('primaryEntityId')]);

  const handleMultipleSelectChange = (
    availableOptions: ISelectValue[],
    selectedOptions: ISelectValue[],
    newSelection: ISelectValue,
    selectionIndex: number
  ): { availableOptions: ISelectValue[]; selectedOptions: ISelectValue[] } => {
    const updatedSelectedOptions = [...selectedOptions];
    updatedSelectedOptions[selectionIndex] = newSelection;

    const filteredAvailableOptions = availableOptions.filter((option) => option.value !== newSelection?.value);

    const previousSelection = selectedOptions[selectionIndex];
    const hasPreviousSelection = previousSelection?.value && previousSelection.value !== '';

    return {
      availableOptions: hasPreviousSelection ? [previousSelection, ...filteredAvailableOptions] : filteredAvailableOptions,
      selectedOptions: updatedSelectedOptions
    };
  };

  const removeMultipleSelectItem = (
    availableOptions: ISelectValue[],
    selectedOptions: ISelectValue[],
    removalIndex: number
  ): { removedItem: ISelectValue; availableOptions: ISelectValue[]; selectedOptions: ISelectValue[] } => {
    const updatedSelectedOptions = [...selectedOptions];
    const removedItem = updatedSelectedOptions.splice(removalIndex, 1)[0];
    const updatedAvailableOptions = removedItem?.value
      ? [...availableOptions, removedItem].sort((a, b) => a.label.localeCompare(b.label))
      : availableOptions;

    return {
      removedItem,
      availableOptions: updatedAvailableOptions,
      selectedOptions: updatedSelectedOptions
    };
  };

  const onSubmit = async (data: IEditGroupForm) => {
    try {
      setErrorMessage(null);

      const { groupName, primaryEntityId, additionalGroupedEntities } = data;
      const additionalEntityIds = additionalGroupedEntities.map((entity) => entity.value);

      if (currentGroup) {
        await updateGroup({
          variables: {
            data: {
              id: currentGroup.groupId,
              name: groupName,
              primaryEntityId: primaryEntityId,
              additionalEntityIds: additionalEntityIds
            }
          }
        });
      } else {
        await createGroup({
          variables: {
            data: {
              name: groupName,
              primaryEntityId,
              additionalEntityIds
            }
          }
        });
      }

      await investorLegalEntitiesRefetch();
      handleCloseModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Entity Grouping" />} />
        <PageTitle title="Edit Group" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <EditInvestorModalPageForm onSubmit={handleSubmit(onSubmit)}>
            <EditInvestorFormFieldsWrapper>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="groupName"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('editGroupModalPage.form.groupName.errorMessage')
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      value={value}
                      onChange={onChange}
                      errorText={error?.message}
                      label={t('editGroupModalPage.form.groupName.label')}
                      size="md"
                      styleType="form"
                      isRequiredField
                    />
                  )}
                />
              </TwoColumnWrapper>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="primaryEntityId"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: t('editGroupModalPage.form.primaryEntityId.errorMessage')
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      setValue={(selectedValue) => {
                        onChange(selectedValue);

                        const additionalGroupedEntities = watch('additionalGroupedEntities');
                        const previousValue = value;

                        if (previousValue) {
                          const previousOption = primaryEntitySelectOptions.find((option) => option.value === previousValue);
                          if (previousOption) {
                            setListUngroupedEntities((prev) => [...prev, previousOption].sort((a, b) => a.label.localeCompare(b.label)));
                          }
                        }

                        const isInAdditionalEntities = additionalGroupedEntities.some((item) => item.value === selectedValue);
                        if (isInAdditionalEntities) {
                          const updatedAdditionalEntities = additionalGroupedEntities.filter((item) => item.value !== selectedValue);
                          onChange(updatedAdditionalEntities);
                        }

                        setListUngroupedEntities((prev) => prev.filter((item) => item.value !== selectedValue));
                      }}
                      options={primaryEntitySelectOptions}
                      errorText={error?.message}
                      label={t('editGroupModalPage.form.primaryEntityId.label')}
                      size="md"
                      styleType="form"
                      isRequiredField
                    />
                  )}
                />
                {!currentGroup && (
                  <Banner
                    icon={<InfoIcon fill={themeVar('font.base')} width={26} height={26} />}
                    title={t('editGroupModalPage.primaryEntityInfoBanner.title')}
                    description={t('editGroupModalPage.primaryEntityInfoBanner.description')}
                    descriptionFontSize="16px"
                    color={themeVar('font.base')}
                    bgColor={themeVar('layer.2')}
                    dividerColor={themeVar('border.base')}
                  />
                )}
                {!!currentGroup && isPrimaryEntityChanged && (
                  <Banner
                    icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                    title={t('editGroupModalPage.primaryEntityWarningBanner.title')}
                    description={t('editGroupModalPage.primaryEntityWarningBanner.description')}
                    descriptionFontSize="16px"
                    color={themeVar('context.light')}
                    bgColor={themeVar('context.warning')}
                    dividerColor={themeVar('context.light')}
                  />
                )}
              </TwoColumnWrapper>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="additionalGroupedEntities"
                  control={control}
                  rules={{
                    validate: (value: ISelectValue[]) => {
                      if (!value || value.length === 0) {
                        return t('editGroupModalPage.form.additionalGroupedEntities.errorMessage');
                      }
                      if (value.some((item) => !item.value || item.value === '')) {
                        return t('editGroupModalPage.form.additionalGroupedEntities.errorMessage');
                      }
                      return true;
                    }
                  }}
                  render={({ field: { onChange, value: additionalGroupedEntities }, fieldState: { error } }) => (
                    <div>
                      <Label size="md" required>
                        {t('editGroupModalPage.form.additionalGroupedEntities.label')}
                      </Label>
                      {additionalGroupedEntities.map((el, index) => (
                        <DynamicSelectWrapper key={`entities-${el.value}`}>
                          <Select
                            value={el.value}
                            previewValue={el.label}
                            setValue={(selectedValue) => {
                              const previousValue = el.value;
                              const primaryEntityId = watch('primaryEntityId');

                              if (previousValue === '') {
                                setHasInteractedWithEmptyOption(true);
                              }

                              if (previousValue) {
                                const previousOption = listUngroupedEntities.find((option) => option.value === previousValue);
                                if (previousOption) {
                                  setPrimaryEntitySelectOptions((prev) => [...prev, previousOption].sort((a, b) => a.label.localeCompare(b.label)));
                                }
                              }

                              if (primaryEntityId === selectedValue) return;

                              const newSelection = listUngroupedEntities.find((item) => item.value === selectedValue) || createSimpleSelectOption('');
                              const { availableOptions, selectedOptions } = handleMultipleSelectChange(
                                listUngroupedEntities,
                                additionalGroupedEntities,
                                newSelection,
                                index
                              );
                              setListUngroupedEntities(availableOptions);
                              setPrimaryEntitySelectOptions((prev) => {
                                return Array.from(new Set([...prev.filter((item) => item.value !== selectedValue), ...availableOptions]));
                              });
                              onChange(selectedOptions);
                            }}
                            options={listUngroupedEntities}
                            styleType={additionalGroupedEntities[additionalGroupedEntities.length - 1] !== el ? 'disabled' : 'form'}
                            size="md"
                            withSearch
                          />
                          <DeleteSelectItem
                            onClick={() => {
                              const { removedItem, availableOptions, selectedOptions } = removeMultipleSelectItem(
                                listUngroupedEntities,
                                additionalGroupedEntities,
                                index
                              );
                              setHasInteractedWithEmptyOption(false);
                              setListUngroupedEntities(availableOptions);
                              onChange(selectedOptions);

                              if (removedItem?.value) {
                                setPrimaryEntitySelectOptions((prev) => [...prev, removedItem].sort((a, b) => a.label.localeCompare(b.label)));
                              }
                            }}
                          >
                            <CloseInfoIcon width={21} height={20} fill={themeVar('font.action')} />
                          </DeleteSelectItem>
                        </DynamicSelectWrapper>
                      ))}
                      {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                      {!!listUngroupedEntities.length && additionalGroupedEntities[additionalGroupedEntities.length - 1]?.value !== '' && (
                        <AddButton
                          text="Add"
                          onClick={() => {
                            setHasInteractedWithEmptyOption(false);
                            return onChange([...additionalGroupedEntities, createSimpleSelectOption('')]);
                          }}
                        />
                      )}
                    </div>
                  )}
                />
              </TwoColumnWrapper>
              <AssetClassBanner>
                <Banner
                  title={t('editGroupModalPage.validationBanner.title')}
                  description={t('editGroupModalPage.validationBanner.description')}
                  icon={
                    isValid ? (
                      <ApprovedIcon fill={themeVar('context.light')} />
                    ) : hasInteractedWithEmptyOption ? (
                      <RejectedBannerIcon fill={themeVar('context.light')} />
                    ) : (
                      <ApprovedIcon fill={themeVar('context.light')} />
                    )
                  }
                  bgColor={
                    isValid ? themeVar('context.success') : hasInteractedWithEmptyOption ? themeVar('context.error') : themeVar('context.success')
                  }
                />
              </AssetClassBanner>
              <EditInvestorFormDevider />
            </EditInvestorFormFieldsWrapper>
            <EditInvestorFormFooter>
              <Button styleType="outline" onClick={handleCloseModalPage} type="button" size="lg">
                {t('common:cancel')}
              </Button>
              <Button styleType="default" onClick={() => setHasInteractedWithEmptyOption(true)} type="submit" size="lg">
                {t('common:save')}
              </Button>
            </EditInvestorFormFooter>
          </EditInvestorModalPageForm>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};

const DeleteSelectItem = styled.div`
  position: relative;
  top: 3px;
  padding-left: 15px;
  cursor: pointer;
`;

const AssetClassBanner = styled.div`
  max-width: 341px;
  width: 100%;
  margin: 50px 0 20px 20px;
`;
